<template>
	<div class="pd-layout" :class="{ 'is-nav-open': navOpen }">
		<div class="pd-progress" aria-hidden="true"><i :style="{ transform: `scaleX(${progress})` }"></i></div>

		<aside class="pd-side" :aria-label="t('User manual')">
			<SideNav :tree="docs.tree" :current="path" :section="docs.section" :home="docs.home" @navigate="navOpen = false" />
		</aside>
		<button class="pd-scrim" type="button" :aria-label="t('Close the menu')" @click="navOpen = false"></button>

		<main class="pd-main">
			<header class="pd-hero">
				<nav class="pd-crumbs" :aria-label="t('Breadcrumb')">
					<a :href="docs.home">{{ t("User manual") }}</a>
					<template v-if="section && section.route !== path">
						<span aria-hidden="true">/</span><a :href="section.route">{{ section.title }}</a>
					</template>
				</nav>
				<h1 class="pd-title">{{ title }}</h1>
				<p v-if="lede" class="pd-lede">{{ lede }}</p>
				<div class="pd-meta">
					<span v-if="section" class="pd-chip pd-chip--accent">{{ section.title }}</span>
					<span v-if="minutes" class="pd-chip">{{ minutes }} {{ t("min read") }}</span>
					<button class="pd-chip pd-chip--button" type="button" @click="copyLink">{{ copied ? t("Link copied") : t("Copy link") }}</button>
				</div>
				<div class="pd-tools">
					<button class="pd-menu" type="button" @click="navOpen = true">{{ t("Sections") }}</button>
					<button class="pd-search-trigger" type="button" @click="searchOpen = true">
						<span>{{ t("Search the manual") }}</span><kbd>{{ shortcut }}</kbd>
					</button>
					<label v-if="docs.languages && docs.languages.length > 1" class="pd-lang">
						<span class="pd-visually-hidden">{{ t("Language") }}</span>
						<select :value="docs.lang" @change="changeLanguage($event.target.value)">
							<option v-for="language in docs.languages" :key="language.code" :value="language.code">{{ language.name }}</option>
						</select>
					</label>
				</div>
			</header>

			<div ref="content" class="pd-content"></div>

			<nav v-if="docs.prev || docs.next" class="pd-pager" :aria-label="t('More pages')">
				<a v-if="docs.prev" class="pd-card" :href="docs.prev.route"><small>{{ t("Previous") }}</small><strong>{{ docs.prev.title }}</strong></a>
				<span v-else></span>
				<a v-if="docs.next" class="pd-card pd-card--next" :href="docs.next.route"><small>{{ t("Next") }}</small><strong>{{ docs.next.title }}</strong></a>
			</nav>
		</main>

		<aside class="pd-aside">
			<Toc :items="headings" :active="activeHeading" :progress="progress" :label="t('On this page')" :top-label="t('Back to top')" />
		</aside>

		<SearchPalette v-if="searchOpen" :pages="pages" :labels="searchLabels" @close="searchOpen = false" />
	</div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import SearchPalette from "./SearchPalette.vue";
import SideNav from "./SideNav.vue";
import Toc from "./Toc.vue";

const props = defineProps({ docs: { type: Object, required: true }, article: { type: Object, required: true } });
const docs = props.docs;
const t = (text) => (window.__ ? window.__(text) : text);
const path = location.pathname.replace(/\/$/, "");
const content = ref(null);
const title = ref(document.title.split(" · ")[0]);
const lede = ref("");
const minutes = ref(0);
const headings = ref([]);
const activeHeading = ref("");
const progress = ref(0);
const navOpen = ref(false);
const searchOpen = ref(false);
const copied = ref(false);
const shortcut = /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘ K" : "Ctrl K";
const searchLabels = { placeholder: t("Search the manual"), empty: t("No page matches"), close: t("Close") };

const section = computed(() => docs.tree.find((s) => s.route === docs.section) || null);
const pages = computed(() =>
	docs.tree.flatMap((s) => s.groups.flatMap((g) => g.pages.map((p) => ({ ...p, section: s.title }))))
);

onMounted(async () => {
	readArticle(props.article);
	content.value.appendChild(props.article);
	const fallback = document.getElementById("pd-static");
	if (fallback) fallback.remove();
	await nextTick();
	window.addEventListener("scroll", onScroll, { passive: true });
	window.addEventListener("keydown", onKey);
	onScroll();
});

onBeforeUnmount(() => {
	window.removeEventListener("scroll", onScroll);
	window.removeEventListener("keydown", onKey);
});

// The page's own title becomes the hero; a leading bold sentence becomes its lede; headings get anchors.
function readArticle(article) {
	const h1 = article.querySelector("h1");
	if (h1) {
		title.value = h1.textContent.trim();
		h1.remove();
	}
	const first = article.querySelector(".from-markdown > p, p");
	if (first && first.children.length === 1 && first.firstElementChild.tagName === "STRONG" && first.textContent.trim() === first.firstElementChild.textContent.trim()) {
		lede.value = first.textContent.trim();
		first.remove();
	}
	minutes.value = Math.max(1, Math.round((article.textContent || "").split(/\s+/).length / 200));
	const used = new Set();
	headings.value = [...article.querySelectorAll("h2, h3")].map((node) => {
		let id = node.id || slug(node.textContent);
		while (used.has(id)) id += "-1";
		used.add(id);
		node.id = id;
		const anchor = document.createElement("a");
		anchor.className = "pd-anchor";
		anchor.href = `#${id}`;
		anchor.setAttribute("aria-label", t("Link to this section"));
		anchor.textContent = "#";
		node.appendChild(anchor);
		return { id, text: node.textContent.replace(/#$/, "").trim(), level: node.tagName === "H3" ? 3 : 2 };
	});
	article.querySelectorAll("pre").forEach(addCopyButton);
	article.querySelectorAll(".btn-next-wrapper").forEach((node) => node.remove()); // the pager below replaces it
}

function addCopyButton(pre) {
	const button = document.createElement("button");
	button.type = "button";
	button.className = "pd-copy";
	button.textContent = t("Copy");
	button.addEventListener("click", () => {
		navigator.clipboard && navigator.clipboard.writeText(pre.innerText.replace(/Copy$/, "").trim());
		button.textContent = t("Copied");
		setTimeout(() => (button.textContent = t("Copy")), 1600);
	});
	pre.appendChild(button);
}

function onScroll() {
	const box = content.value && content.value.getBoundingClientRect();
	if (!box) return;
	const total = Math.max(1, box.height - window.innerHeight * 0.6);
	progress.value = Math.min(1, Math.max(0, -box.top / total));
	// the section being read: the last heading that has reached the top of the window
	const line = (parseInt(getComputedStyle(document.documentElement).getPropertyValue("--header-h"), 10) || 72) + 40;
	let current = headings.value.length ? headings.value[0].id : "";
	for (const heading of headings.value) {
		const node = document.getElementById(heading.id);
		if (node && node.getBoundingClientRect().top <= line) current = heading.id;
	}
	activeHeading.value = current;
}

function onKey(event) {
	const typing = /INPUT|TEXTAREA|SELECT/.test((event.target && event.target.tagName) || "");
	if ((event.key === "k" && (event.metaKey || event.ctrlKey)) || (event.key === "/" && !typing)) {
		event.preventDefault();
		searchOpen.value = true;
	}
	if (event.key === "Escape") navOpen.value = false;
}

function changeLanguage(code) {
	const language = docs.languages.find((l) => l.code === code);
	if (language) location.href = language.route;
}

async function copyLink() {
	try {
		await navigator.clipboard.writeText(location.href.split("#")[0]);
		copied.value = true;
		setTimeout(() => (copied.value = false), 1800);
	} catch (error) {
		copied.value = false;
	}
}

function slug(text) {
	return (text || "section").toLowerCase().trim().replace(/[^\p{L}\p{N}]+/gu, "-").replace(/^-|-$/g, "") || "section";
}
</script>
